import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
  className?: string;
  labelledBy?: string;
}

const sizeClasses = {
  sm: 'lg:max-w-sm lg:h-auto lg:max-h-[90vh] h-full',
  md: 'lg:max-w-md lg:h-auto lg:max-h-[90vh] h-full',
  lg: 'lg:max-w-lg lg:h-auto lg:max-h-[90vh] h-full',
  xl: 'lg:max-w-xl lg:h-auto lg:max-h-[90vh] h-full',
  '2xl': 'lg:max-w-2xl lg:h-auto lg:max-h-[90vh] h-full',
  '3xl': 'lg:max-w-3xl lg:h-auto lg:max-h-[90vh] h-full',
  '4xl': 'lg:max-w-4xl lg:h-auto lg:max-h-[90vh] h-full',
  '5xl': 'lg:w-full h-full',
  '6xl': 'lg:w-full h-full',
};

// Stack of currently open modals so Escape only closes the topmost one
// (e.g. a confirm dialog layered over an edit modal).
const openModalStack: symbol[] = [];

export const Modal = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  className,
  labelledBy,
}: ModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    // Move focus into dialog for keyboard users.
    const timer = window.setTimeout(() => {
      const focusable = dialogRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(timer);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 transition-opacity dark:bg-black/70 lg:p-5"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={cn(
          'relative flex max-h-[90vh] w-full flex-col overflow-hidden bg-card-color-100 dark:bg-gray-800',
          'lg:rounded-xl',
          'shadow-2xl border border-gray-200 dark:border-gray-700',
          sizeClasses[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
      >
        {children}
      </div>
    </div>
  );
};

const ModalHeader = ({
  title,
  onClose,
  description,
}: {
  title: string;
  onClose: () => void;
  description?: React.ReactNode;
}) => {
  const { t } = useTranslation('Common');
  return (
    <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 p-6 dark:border-gray-700">
      <div className="min-w-0 pr-2">
        <h2
          id="modal-title"
          className="text-xl font-semibold text-title-page-color-100 dark:text-white"
        >
          {title}
        </h2>
        {description ? (
          <div className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">
            {description}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
        aria-label={t('Close')}
      >
        <X size={20} />
      </button>
    </div>
  );
};

const ModalBody = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn('min-h-0 flex-1 overflow-y-auto p-6', className)}>
    {children}
  </div>
);

const ModalFooter = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-gray-700',
      className
    )}
  >
    {children}
  </div>
);

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
